import { useMsal } from '@azure/msal-react';
import { Button, List, ListItem, Popover, PopoverSurface, PopoverTrigger, Slot, Spinner, Text } from '@fluentui/react-components';
import { AsYouType, CountryCode, getCountries, parsePhoneNumberWithError } from 'libphonenumber-js';
import { observer } from 'mobx-react-lite';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useStores } from '../../../common/hooks';
import { Country } from '../../../models';
import { FlexBox } from '../flex-box';
import { FormInput } from '../form';
import { Chevron } from '../icons';
import { LoadingSpinner } from '../LoadingSpinner';
import { PhoneNumberFieldId } from './phone-number';
import './PhoneNumberInput.scss';
const PHONE_NUMBER_REGEX = /\(\d+(?![^)]*\))/g;
const DIGITS_ONLY_REGEX = /\D/g;
const MIN_DIGITS_FOR_COUNTRY_DETECTION = 3;

interface PhoneNumberInputProps {
  id: PhoneNumberFieldId;
  value?: string;
  onChange?: (phoneNumber: string) => void;
  onCountryChange?: (id: PhoneNumberFieldId, countryCode: CountryCode) => void;
  disabled?: boolean;
  error?: Slot<'div'>;
  label?: string;
  required?: boolean;
  readonly?: boolean;
  ariaLabelledBy?: string; 
}

const CountryFlag = memo<{ countryCode: string; alt?: string }>(({ countryCode, alt }) => (
  <img
    src={`https://flagcdn.com/${countryCode.toLowerCase()}.svg`}
    alt={alt || `${countryCode} flag`}
    width={24}
    height={24}
    aria-hidden
    loading='lazy'
  />
));

const PhoneNumberInputImpl: React.FC<PhoneNumberInputProps> = (props) => {
  const {
    id,
    value = '',
    onChange,
    onCountryChange,
    disabled = false,
    error,
    label,
    required = false,
    readonly = false,
    ariaLabelledBy,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    countryStore,
  } = useStores();

  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>('US');
  const [phoneNumber, setPhoneNumber] = useState<string>(value);
  const [searchText, setSearchText] = useState<string>('');
  const [isShowDropdown, setIsShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const inputNumberRef = useRef<HTMLInputElement>(null);

  const getGlobalCountries = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const countries = await apiStore.getCountries(instance, 'global', searchText);
      countryStore.setCountries('global', countries);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const getCountriesWithSameDialCode = (dialCode: string): CountryCode[] => {
    const countries = getCountries();
    return countries.filter((country) => {
      try {
        return countryStore.getDialCode(country) === dialCode;
      } catch {
        return false;
      }
    });
  };

  const detectCountryFromPhoneNumber = (phoneNumber: string, currentCountry: CountryCode): CountryCode => {
    if (!phoneNumber.trim()) return currentCountry;

    try {
      const parsedWithoutHint = parsePhoneNumberWithError(phoneNumber);

      if (parsedWithoutHint?.country && parsedWithoutHint.isValid()) {
        return parsedWithoutHint.country as CountryCode;
      }
    } catch (error) {
      // TODO: Handle error
    }

    try {
      const currentDialCode = countryStore.getDialCode(currentCountry);
      const countriesWithSameDialCode = getCountriesWithSameDialCode(currentDialCode);

      let bestMatch: { country: CountryCode; confidence: number } | null = null;

      for (const country of countriesWithSameDialCode) {
        try {
          const parsed = parsePhoneNumberWithError(phoneNumber, country);
          if (parsed && parsed.isValid()) {
            const confidence = parsed.country === country ? 2 : 1;

            if (!bestMatch || confidence > bestMatch.confidence) {
              bestMatch = { country, confidence };
            }
          }
        } catch (error) {
          continue;
        }
      }

      if (bestMatch) {
        return bestMatch.country;
      }
    } catch (error) {
      // TODO: Handle error
    }

    return currentCountry;
  };

  const handlePhoneNumberChange = (value: string): void => {
    if (readonly) return;

    const currentInputRef = inputNumberRef.current;
    if (!selectedCountryCode || !currentInputRef) return;

    let inputValue = value;
    const currentDialCode = countryStore.getDialCode(selectedCountryCode);

    if (!inputValue.startsWith(currentDialCode)) {
      inputValue = currentDialCode + ' ' + inputValue.replace(/^\+\d+\s*/, '');
    }

    const cursorPosition = currentInputRef.selectionStart || 0;
    const dialCodeLength = currentDialCode.length + 1;

    if (cursorPosition < dialCodeLength) {
      setTimeout(() => {
        currentInputRef.setSelectionRange(dialCodeLength, dialCodeLength);
      }, 0);
    }

    const match = inputValue.match(PHONE_NUMBER_REGEX);

    let formattedNumber = inputValue;

    if (match) {
      formattedNumber = inputValue.replace('(', '');
    }

    try {
      const parsedNumber = parsePhoneNumberWithError(formattedNumber, selectedCountryCode);

      if (parsedNumber && parsedNumber.isValid()) {
        formattedNumber = parsedNumber.formatInternational();

        if (parsedNumber.country && parsedNumber.country !== selectedCountryCode) {
          setSelectedCountryCode(parsedNumber.country);
          onCountryChange?.(id, parsedNumber.country);
        }
      } else {
        const asYouType = new AsYouType(selectedCountryCode);
        const partiallyFormatted = asYouType.input(formattedNumber);

        if (asYouType.isValid()) {
          const phoneNumber = asYouType.getNumber();
          if (phoneNumber) {
            formattedNumber = phoneNumber.formatInternational();
          }
        } else {
          formattedNumber = partiallyFormatted;
        }
      }
    } catch (error) {
      const asYouType = new AsYouType(selectedCountryCode);
      formattedNumber = asYouType.input(formattedNumber);
    }

    if (!formattedNumber.startsWith(currentDialCode)) {
      formattedNumber = currentDialCode + ' ' + formattedNumber.replace(/^\+\d+\s*/, '');
    }

    setPhoneNumber(formattedNumber);

    if (onChange) onChange(formattedNumber);
  };

  const hideDropdown = (): void => {
    setIsShowDropdown(false);
    setSearchText('');
  };

  const extractLocalNumber = (number: string): string | undefined => {
    try {
      const parsed = parsePhoneNumberWithError(number);
      if (parsed) {
        return parsed.nationalNumber;
      }
    } catch (error) {
      // TODO: Handle error
    }

    return number
      .replace(/^\+\d{1,4}\s*/, '')
      .replace(/^\(\d+\)\s*/, '')
      .replace(/^[\s\-\(\)]+/, '')
      .trim();
  };

  const handleCountrySelect = (country: Country): void => {
    if (readonly) return;

    setSelectedCountryCode(country.countryCode as CountryCode);
    onCountryChange?.(id, country.countryCode as CountryCode);
    const newDialCode = countryStore.getDialCode(country.countryCode);

    if (phoneNumber) {
      const localNumber = extractLocalNumber(phoneNumber);

      if (localNumber) {
        try {
          const testNumber = localNumber.startsWith(newDialCode.replace('+', ''))
            ? `+${localNumber}`
            : `${newDialCode}${localNumber.replace(/^0+/, '')}`;

          const parsed = parsePhoneNumberWithError(testNumber, country.countryCode as CountryCode);

          if (parsed && parsed.isValid()) {
            const formatted = parsed.formatInternational();
            setPhoneNumber(formatted);
            if (onChange) onChange(formatted);

            if (parsed.country) {
              setSelectedCountryCode(parsed.country);
              onCountryChange?.(id, parsed.country);
            }
          } else {
            const fallbackNumber = `${newDialCode} ${localNumber}`;
            setPhoneNumber(fallbackNumber);
            if (onChange) onChange(fallbackNumber);
          }
        } catch (error) {
          const fallbackNumber = `${newDialCode} ${localNumber}`;
          setPhoneNumber(fallbackNumber);
          if (onChange) onChange(fallbackNumber);
        }
      } else {
        setPhoneNumber(newDialCode + ' ');
        if (onChange) onChange(newDialCode + ' ');
      }
    } else {
      setPhoneNumber(newDialCode + ' ');
      if (onChange) onChange(newDialCode + ' ');
    }

    hideDropdown();
  };

  const renderDropdownOptions = (id: string): React.JSX.Element => (
    <div role='group'>
      <FormInput
        onChange={(value) => setSearchText(value)}
        inputProps={{
          placeholder: "Search countries...",
          role: 'combobox',
          'aria-expanded': true,
          'aria-controls': `${id}list`,
        }}
      />
      <List
        id={`${id}list`}
        navigationMode='items'
        role='listbox'
        aria-label='countries list'
      >
        {isLoading && <LoadingSpinner />}
        {!isLoading && countryStore.countries.length === 0 && (
          <ListItem
            value=''
          >
            No countries found.
          </ListItem>
        )}
        {!isLoading && countryStore.countries.map((country) => (
          <ListItem
            key={country.countryId}
            aria-label={country.displayName}
            onAction={() => handleCountrySelect(country)}
          >
            <CountryFlag
              countryCode={country.countryCode}
              alt={`${country.displayName} flag`}
            />
            <Text>{`${country.displayName} (${countryStore.getDialCode(country.countryCode)})`}</Text>
          </ListItem>
        ))}
      </List>
    </div>
  );

  useEffect(() => {
    if (value !== phoneNumber || !readonly) {
      let formattedValue = value || '';
      const currentDialCode = countryStore.getDialCode(selectedCountryCode);

      if (!formattedValue.startsWith('+')) {
        formattedValue = currentDialCode + ' ' + formattedValue;
      }

      if (formattedValue && formattedValue.trim()) {
        try {
          const digitsOnly = formattedValue.replace(DIGITS_ONLY_REGEX, '');

          if (digitsOnly.length >= MIN_DIGITS_FOR_COUNTRY_DETECTION) {
            const detectedCountry = detectCountryFromPhoneNumber(formattedValue, selectedCountryCode);

            const parsed = parsePhoneNumberWithError(formattedValue, detectedCountry);

            if (parsed && parsed.isValid()) {
              formattedValue = parsed.formatInternational();

              if (detectedCountry !== selectedCountryCode) {
                setSelectedCountryCode(detectedCountry);
                onCountryChange?.(id, detectedCountry);
              }
            }
          }
        } catch (error) {
          // TODO: Handle error
        }
      } else {
        formattedValue = currentDialCode + ' ';
      }

      setPhoneNumber(formattedValue);
    }
  }, [value, readonly]);

  useEffect(() => {
    getGlobalCountries();
  }, [searchText]);

  return (
    <div className='phone-number-input'>
      <Popover
        inline
        open={isShowDropdown}
        closeOnScroll
        onOpenChange={() => {
          setSearchText('');
          setIsShowDropdown(!isShowDropdown);
        }}
        positioning='below-start'
      >
        <FlexBox>
          <FormInput
            validationMessage={error}
            label={label}
            required={required}
            onChange={(value) => handlePhoneNumberChange(value)}
            inputRef={inputNumberRef}
            inputProps={{
              id: `${id}-input-number`,
              name: `${id}-input-number`,
              type: 'text',
              value: phoneNumber,
              'aria-labelledby': ariaLabelledBy,
              readOnly: readonly,
              disabled,
              contentBefore: (
                <PopoverTrigger>
                  <Button
                    size='small'
                    appearance='primary'
                    disabled={disabled || isLoading || readonly}
                    onClick={() => setIsShowDropdown(!isShowDropdown)}
                    type='button'
                    aria-expanded={isShowDropdown}
                  >
                    {isLoading && <Spinner size='extra-tiny' />}
                    {selectedCountryCode && (
                      <>
                        <CountryFlag
                          countryCode={selectedCountryCode}
                          alt={`${selectedCountryCode} flag`}
                        />
                        <span>{countryStore.getDialCode(selectedCountryCode)}</span>
                      </>
                    )}
                    <Chevron
                      fill='#ffffff'
                      iconDirection={isShowDropdown ? 'DOWN' : 'RIGHT'}
                      sideSize={10}
                    />
                  </Button>
                </PopoverTrigger>
              ),
            }}
          />
        </FlexBox>
        <PopoverSurface
          className='phone-number-input__countries'
        >
          {renderDropdownOptions('countries-dropdown')}
        </PopoverSurface>
      </Popover>
    </div>
  );
};

export const PhoneNumberInput = observer(PhoneNumberInputImpl);
